import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db, auth } from './firebase';
import {
  collection,
  onSnapshot,
  query,
  addDoc,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  orderBy
} from './firestore-compat';
import {
  addDays,
  addWeeks,
  addMonths,
  isBefore,
  isEqual,
  format
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AddAuditoriaModal from './AddAuditoriaModal';
import DeadlineReminder from './components/DeadlineReminder';
import { useDeadlineReminder } from './hooks/useDeadlineReminder';
import JustificativaModal from './JustificativaModal';
import BloquearDiaModal from './BloquearDiaModal';
import DesbloquearDiaModal from './DesbloquearDiaModal';
import {
  pedirPermissaoNotificacao,
  verificarAgendaParaNotificacoes
} from './notifications';
import NotificationsPopup from './components/NotificationsPopup';

// ==========================
// FUNÇÃO PARA GERAR GRADE
// ==========================
const generateWeekdayGrid = (date) => {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  let dayOfWeek = firstDayOfMonth.getDay();
  let diff = firstDayOfMonth.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const gridStartDate = new Date(firstDayOfMonth.setDate(diff));

  const monthDays = [];
  let currentDate = new Date(gridStartDate);

  for (let i = 0; i < 35; i++) { // Aumentei para 35 para garantir 5 semanas visuais se necessário
    monthDays.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
    if (currentDate.getDay() === 6) {
      currentDate.setDate(currentDate.getDate() + 2);
    }
  }
  return monthDays;
};

// ==========================
// CLASSE DE STATUS
// ==========================
const getAuditoriaStatusClass = (auditoria) => {
  if (auditoria.isManualPlaceholder) return 'status-demanda-interna';
  if (auditoria.status === 'Feito') return 'status-feito';
  if (auditoria.status === 'Não Realizado' || auditoria.justificativaNaoRealizado)
    return 'status-nao-realizado';

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dataAuditoria = new Date(auditoria.data + 'T00:00:00');

  if (dataAuditoria < hoje && auditoria.status === 'Agendado')
    return 'status-nao-realizado';

  return '';
};

// ==========================
// COMPONENTE PRINCIPAL
// ==========================
function AgendaHomecare() {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Hook para detectar mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // DADOS DINÂMICOS
  const [auditoresConfig, setAuditoresConfig] = useState([]);
  const [activeAuditores, setActiveAuditores] = useState([]); // Array de IDs ou Nomes

  const [activeSectors, setActiveSectors] = useState([]); // Filtro de Setores
  const [availableSectors, setAvailableSectors] = useState([]); // Setores encontrados nos dados

  const [auditorias, setAuditorias] = useState([]);
  const [bloqueios, setBloqueios] = useState([]);

  const auditoriasReminders = useMemo(() => {
    return auditorias.map(a => ({
      ...a,
      start: a.data,
      title: `${a.horario} - ${a.descricao || a.auditoria || 'Auditoria'}`
    }));
  }, [auditorias]);

  const { isTodayOpen, isWeekOpen, todayItems, weekItems, closeTodayPopup, closeWeekPopup, dismissToday, openReminderManually } = useDeadlineReminder(auditoriasReminders, 'agenda_homecare', 'event');

  // FILTROS AVANÇADOS
  const [filterPendingFuture, setFilterPendingFuture] = useState(false); // A Realizar (Futuro)
  const [filterPendingPast, setFilterPendingPast] = useState(false);     // Não Realizados (Passado)

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAuditoria, setEditingAuditoria] = useState(null);

  const [isJustificativaModalOpen, setIsJustificativaModalOpen] = useState(false);
  const [auditoriaParaJustificar, setAuditoriaParaJustificar] = useState(null);

  const [isBloqueioModalOpen, setIsBloqueioModalOpen] = useState(false);
  const [isDesbloqueioModalOpen, setIsDesbloqueioModalOpen] = useState(false);

  // DEBUG MODE
  const [debugMode, setDebugMode] = useState(false);

  const [userName, setUserName] = useState('');
  const currentUser = auth.currentUser;

  // Estados para sistema de notificações
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notificationsCount, setNotificationsCount] = useState(0);

  const monthDays = generateWeekdayGrid(currentDate);

  // ==========================
  // CARREGAR DADOS
  // ==========================
  useEffect(() => {
    // Carregar Auditores Homecare
    const unsubAuditors = onSnapshot(query(collection(db, 'homecare_auditors_config'), orderBy('name')), (snap) => {
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAuditoresConfig(loaded);
      // Se a lista estiver vazia (primeiro load), seta todos. Se já tiver seleção, mantém.
      setActiveAuditores(prev => prev.length === 0 ? loaded.map(a => a.name) : prev);
    });

    return () => {
      unsubAuditors();
    };
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser && currentUser.email) {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          setUserName(querySnapshot.docs[0].data().name);
        }
      }
    };

    pedirPermissaoNotificacao().then(fetchUserData);
  }, [currentUser?.email]);

  useEffect(() => {
    const unsubAuditorias = onSnapshot(
      query(collection(db, 'auditorias_homecare')),
      (snapshot) => {
        const loadedAudits = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: 'auditoria'
        }));
        setAuditorias(loadedAudits);

        // Extrair setores únicos para o filtro
        const sectors = [...new Set(loadedAudits.map(a => a.setor).filter(Boolean))].sort();
        setAvailableSectors(sectors);
        setActiveSectors(prev =>
          prev.length === 0
            ? sectors
            : [...new Set([...prev, ...sectors])] // Adiciona novos setores mantendo os existentes
        );
      }
    );

    const unsubBloqueios = onSnapshot(
      query(collection(db, 'bloqueios_homecare')),
      (snapshot) => {
        setBloqueios(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            type: 'bloqueio'
          }))
        );
      }
    );

    return () => {
      unsubAuditorias();
      unsubBloqueios();
    };
  }, []);

  // Listener de notificações em tempo real
  useEffect(() => {
    if (!currentUser) return;

    const qNotif = query(
      collection(db, 'notificacoes'),
      where('destinatarioUid', '==', currentUser.uid),
      where('tipo', '==', 'recusa_convite'),
      where('lida', '==', false)
    );

    const unsubNotif = onSnapshot(qNotif, (snapshot) => {
      setNotificationsCount(snapshot.docs.length);
    });

    return () => unsubNotif();
  }, [currentUser?.uid]);

  // ==========================
  // NOTIFICAÇÕES
  // ==========================
  useEffect(() => {
    if (userName && (auditorias.length > 0 || bloqueios.length > 0)) {
      const intervalId = setInterval(() => {
        verificarAgendaParaNotificacoes(auditorias, userName);
      }, 60000);
      return () => clearInterval(intervalId);
    }
  }, [auditorias, bloqueios, userName]);

  // ==========================
  // NAVEGAÇÃO
  // ==========================
  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1, 1)));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1, 1)));

  // ==========================
  // FILTROS
  // ==========================
  const toggleSetor = (sectorName) => {
    setActiveSectors((prev) =>
      prev.includes(sectorName)
        ? prev.length === 1 ? prev : prev.filter((s) => s !== sectorName)
        : [...prev, sectorName]
    );
  };

  const toggleAuditor = (auditorName) => {
    setActiveAuditores((prev) =>
      prev.includes(auditorName)
        ? prev.length === 1 ? prev : prev.filter((a) => a !== auditorName)
        : [...prev, auditorName]
    );
  };

  // ==========================
  // MODAIS
  // ==========================
  const handleOpenModalForNew = () => {
    setEditingAuditoria(null);
    setIsModalOpen(true);
  };
  const handleOpenModalForEdit = (auditoria) => {
    setEditingAuditoria(auditoria);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAuditoria(null);
  };

  // ==========================
  // LOGICA MULTI-AUDITORES
  // ==========================
  // Helper para verificar conflitos considerando array de auditores
  const checkForConflict = async (data, auditoresList, horario) => {
    if (!auditoresList || auditoresList.length === 0) return { conflict: false };

    for (const auditor of auditoresList) {
      const qBloqueio = query(
        collection(db, 'bloqueios_homecare'),
        where('auditor', '==', auditor),
        where('data', '==', data)
      );
      const bloqueioSnapshot = await getDocs(qBloqueio);
      if (!bloqueioSnapshot.empty) {
        return { conflict: true, reason: `Dia bloqueado para ${auditor}` };
      }

      const qAuditoriaArray = query(
        collection(db, 'auditorias_homecare'),
        where('auditores', 'array-contains', auditor),
        where('data', '==', data),
        where('horario', '==', horario)
      );
      const snapArray = await getDocs(qAuditoriaArray);
      if (!snapArray.empty) return { conflict: true, reason: `Horário ocupado para ${auditor}` };
    }

    return { conflict: false };
  };

  // ==========================
  // SALVAR / EDITAR
  // ==========================
  const handleAddAuditoria = async (newAuditoria, recorrenciaData) => {
    // newAuditoria.auditores deve ser um array. Se vier só .auditor, converte.
    const auditoresList = newAuditoria.auditores || (newAuditoria.auditor ? [newAuditoria.auditor] : []);

    if (!recorrenciaData.isRecorrente) {
      let conflictResult = { conflict: false };
      try {
        conflictResult = await checkForConflict(newAuditoria.data, auditoresList, newAuditoria.horario);
      } catch (conflictErr) {
        console.warn('[AgendaHomecare] checkForConflict falhou, salvando sem checar conflito:', conflictErr.message);
      }
      if (conflictResult.conflict) {
        alert(`Conflito: ${conflictResult.reason}`);
        return;
      }
      // Deixa o erro propagar para handleSubmit (que trata e mantém o modal aberto)
      await addDoc(collection(db, 'auditorias_homecare'), {
        ...newAuditoria,
        auditores: auditoresList,
        reagendadoVezes: 0,
        status: 'Agendado'
      });
      return;
    }

    // Recorrência
    const batch = writeBatch(db);
    let dataAtual = new Date(newAuditoria.data + 'T00:00:00');
    const dataFinal = new Date(recorrenciaData.terminaEm + 'T00:00:00');
    let conflitos = [];

    while (isBefore(dataAtual, dataFinal) || isEqual(dataAtual, dataFinal)) {
      if (dataAtual.getDay() !== 6 && dataAtual.getDay() !== 0) {
        const dataStr = dataAtual.toISOString().split('T')[0];

        const conflict = await checkForConflict(dataStr, auditoresList, newAuditoria.horario);
        if (conflict.conflict) {
          conflitos.push(`${dataStr}: ${conflict.reason}`);
        } else {
          const docRef = doc(collection(db, 'auditorias_homecare'));
          batch.set(docRef, {
            ...newAuditoria,
            data: dataStr,
            auditores: auditoresList,
            reagendadoVezes: 0,
            status: 'Agendado'
          });
        }
      }

      // Incremento
      switch (recorrenciaData.frequencia) {
        case 'diaria': dataAtual = addDays(dataAtual, 1); break;
        case 'semanal': dataAtual = addWeeks(dataAtual, 1); break;
        case 'quinzenal': dataAtual = addWeeks(dataAtual, 2); break;
        case 'mensal': dataAtual = addMonths(dataAtual, 1); break;
        default: dataAtual = addDays(dataFinal, 1);
      }
    }

    if (conflitos.length > 0) {
      alert(`Alguns agendamentos recorrentes não foram criados por conflito:\n${conflitos.join('\n')}`);
    }
    await batch.commit();
  };

  const handleUpdateAuditoria = async (auditoriaOriginal, dadosAtualizados) => {
    const auditoriaRef = doc(db, 'auditorias_homecare', auditoriaOriginal.id);
    let reagendadoVezes = auditoriaOriginal.reagendadoVezes || 0;
    if (auditoriaOriginal.data !== dadosAtualizados.data) reagendadoVezes++;

    await updateDoc(auditoriaRef, {
      ...dadosAtualizados,
      reagendadoVezes
    });
  };

  const handleDeleteAuditoria = async (id) => await deleteDoc(doc(db, 'auditorias_homecare', id));

  const handleDuplicateAuditoria = async (e, auditoria) => {
    e.stopPropagation(); // Evita abrir o modal de edição
    const { id, ...dadosParaDuplicar } = auditoria;
    await addDoc(collection(db, 'auditorias_homecare'), {
      ...dadosParaDuplicar,
      reagendadoVezes: 0,
      status: 'Agendado'
    });
  };

  const handleSaveBloqueio = async (bloqueioData) => {
    // Verificar se já existe
    const q = query(
      collection(db, 'bloqueios_homecare'),
      where('auditor', '==', bloqueioData.auditor),
      where('data', '==', bloqueioData.data)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      alert('Dia já bloqueado para este auditor.');
      return;
    }
    await addDoc(collection(db, 'bloqueios_homecare'), bloqueioData);
  };

  const handleStatusChange = async (newStatus) => {
    if (!editingAuditoria) return;
    if (newStatus === 'Não Realizado') {
      setAuditoriaParaJustificar(editingAuditoria);
      setIsJustificativaModalOpen(true);
      setIsModalOpen(false);
    } else {
      await updateDoc(doc(db, 'auditorias_homecare', editingAuditoria.id), {
        status: newStatus,
        dataConclusao: new Date().toISOString().split('T')[0],
        justificativaNaoRealizado: null
      });

      // --- NOVO: Propagar o status para a agenda_pessoal ---
      try {
        const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', editingAuditoria.id));
        const snapPes = await getDocs(qPes);
        snapPes.forEach(async (d) => {
          await updateDoc(doc(db, 'agenda_pessoal', d.id), {
            status: newStatus,
            dataConclusao: new Date().toISOString().split('T')[0],
            justificativaNaoRealizado: null
          });
        });
      } catch (pesErr) {
        console.error('[STATUS] Erro ao propagar status para agenda_pessoal:', pesErr);
      }
      // --------------------------------------------------------

      handleCloseModal();
    }
  };

  const handleSaveJustificativa = async (justificativa) => {
    if (!auditoriaParaJustificar) return;
    await updateDoc(doc(db, 'auditorias_homecare', auditoriaParaJustificar.id), {
      status: 'Não Realizado',
      justificativaNaoRealizado: justificativa,
      dataConclusao: null
    });

    // --- NOVO: Propagar justificativa para a agenda_pessoal ---
    try {
      const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', auditoriaParaJustificar.id));
      const snapPes = await getDocs(qPes);
      snapPes.forEach(async (d) => {
        await updateDoc(doc(db, 'agenda_pessoal', d.id), {
          status: 'Não Realizado',
          justificativaNaoRealizado: justificativa,
          dataConclusao: null
        });
      });
    } catch (pesErr) {
      console.error('[STATUS] Erro ao propagar justificativa para agenda_pessoal:', pesErr);
    }
    // --------------------------------------------------------

    setIsJustificativaModalOpen(false);
    setAuditoriaParaJustificar(null);
    handleCloseModal();
  };

  const handleDesbloquearDia = async (data, auditor) => {
    if (!window.confirm(`Desbloquear o dia ${data} para ${auditor}?`)) return;

    const q = query(collection(db, 'bloqueios_homecare'), where('data', '==', data), where('auditor', '==', auditor));
    const snap = await getDocs(q);
    snap.forEach(async (d) => {
      await deleteDoc(doc(db, 'bloqueios_homecare', d.id));
    });
  };

  // Função para reagendar evento recusado
  const handleReagendar = (eventoId, eventoCollection) => {
    // Buscar o evento para editar
    const evento = auditorias.find(e => e.id === eventoId);
    if (evento) {
      handleOpenModalForEdit(evento);
    } else {
      alert('Evento não encontrado. Pode ter sido excluído.');
    }
  };

  // ==========================
  // RENDER HELPERS
  // ==========================
  const getCorAuditor = (nomeAuditor) => {
    const a = auditoresConfig.find(ac => ac.name === nomeAuditor);
    return a ? a.color : '#ccc';
  };

  return (
    <div className="agenda-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#555', margin: 0 }}>Agenda Homecare</h2>
        <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
          <button className="reminder-btn" style={{ backgroundColor: '#e67e22', color: 'white' }} onClick={openReminderManually}>
            🔔 Próximos Vencimentos
          </button>
          <button className="bloquear-dia-btn" onClick={() => setIsBloqueioModalOpen(true)}>Bloquear Dia</button>
          <button className="bloquear-dia-btn" style={{ backgroundColor: '#6c757d' }} onClick={() => setIsDesbloqueioModalOpen(true)}>Desbloquear Dia</button>
          <button className="novo-agendamento-btn" onClick={handleOpenModalForNew}>+ Nova Reunião Externa</button>
        </div>
      </div>

      <AddAuditoriaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddAuditoria={handleAddAuditoria}
        onUpdateAuditoria={handleUpdateAuditoria}
        onDeleteAuditoria={handleDeleteAuditoria}
        onStatusChange={handleStatusChange}
        editingAuditoria={editingAuditoria}
        unidadesConfig={useMemo(() => [], [])} // Sem unidades no Homecare
        auditoresConfig={auditoresConfig}
        type="homecare"
      />

      <JustificativaModal isOpen={isJustificativaModalOpen} onClose={() => setIsJustificativaModalOpen(false)} onSave={handleSaveJustificativa} />
      <BloquearDiaModal isOpen={isBloqueioModalOpen} onClose={() => setIsBloqueioModalOpen(false)} onSave={handleSaveBloqueio} auditoresList={auditoresConfig.map(a => a.name)} />
      <DesbloquearDiaModal isOpen={isDesbloqueioModalOpen} onClose={() => setIsDesbloqueioModalOpen(false)} bloqueios={bloqueios} onDesbloquear={handleDesbloquearDia} />

      <DesbloquearDiaModal isOpen={isDesbloqueioModalOpen} onClose={() => setIsDesbloqueioModalOpen(false)} bloqueios={bloqueios} onDesbloquear={handleDesbloquearDia} />

      <main className="agenda-main">
        {isMobile ? (() => {
          // Helper para renderMobileLayout inline ou chamada de funcao
          return (
            <div className="agenda-mobile-list">
              <div className="agenda-controls mb-3" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                <button onClick={handlePrevMonth} className="btn btn-sm btn-outline-secondary">&lt;</button>
                <h4 className="mb-0 text-capitalize">{format(currentDate, 'MMMM yyyy', { locale: ptBR })}</h4>
                <button onClick={handleNextMonth} className="btn btn-sm btn-outline-secondary">&gt;</button>
              </div>

              {monthDays.filter(day => day.getMonth() === currentDate.getMonth()).map(day => {
                const dayString = day.toISOString().split('T')[0];
                const eventosDia = auditorias.filter(a => a.data === dayString).sort((a, b) => (a.horario || '').localeCompare(b.horario || ''));
                const bloqueioDia = bloqueios.find(b => b.data === dayString);

                if (eventosDia.length === 0 && !bloqueioDia) return null;

                return (
                  <div key={dayString} className="mobile-day-group" style={{ marginBottom: '15px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '10px' }}>
                      <span style={{ fontWeight: 'bold', color: '#00897b', textTransform: 'capitalize' }}>{format(day, 'EEEE, dd/MM', { locale: ptBR })}</span>
                      {bloqueioDia && <span className="badge bg-danger">Bloqueado: {bloqueioDia.auditor}</span>}
                    </div>

                    {eventosDia.length > 0 ? (
                      <div className="mobile-events-list">
                        {eventosDia.map(auditoria => {
                          const statusClass = getAuditoriaStatusClass(auditoria);
                          // Homecare pode ter multiplos auditores, tratar display
                          const auditoresDisplay = Array.isArray(auditoria.auditores) ? auditoria.auditores.join(', ') : auditoria.auditor;
                          // Cor do primeiro auditor
                          const firstAuditor = Array.isArray(auditoria.auditores) ? auditoria.auditores[0] : auditoria.auditor;

                          return (
                            <div
                              key={auditoria.id}
                              className="mobile-event-card"
                              style={{ padding: '10px', backgroundColor: '#f9f9f9', marginBottom: '8px', borderRadius: '6px', borderLeft: `4px solid ${getCorAuditor(firstAuditor)}`, color: '#333' }}
                              onClick={() => handleOpenModalForEdit(auditoria)}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <strong>{auditoria.horario}</strong>
                                <span className="badge bg-light text-dark">{auditoresDisplay}</span>
                              </div>
                              <div style={{ marginTop: '5px' }}>{auditoria.setor || 'N/A'}</div>
                              <div className="small text-muted">{auditoria.descricao || auditoria.auditoria}</div>
                              <div className="small mt-1">Status: {auditoria.status}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-muted small text-center">Apenas Bloqueio</div>
                    )}
                  </div>
                );
              })}

              {auditorias.filter(a => {
                const d = new Date(a.data + 'T00:00:00');
                return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
              }).length === 0 && (
                  <div className="text-center p-4 text-muted">Nenhum agendamento neste mês.</div>
                )}
            </div>
          );
        })() : (
          <>
            <div className="agenda-controls" style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '15px',
              backgroundColor: '#fff',
              padding: '10px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              marginBottom: '20px'
            }}>

              {/* Navegação de Mês */}
              <div className="week-navigation" style={{ display: 'flex', alignItems: 'center', gap: '5px', margin: 0 }}>
                <button onClick={handlePrevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#555' }}>&lt;</button>
                <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: '140px', textAlign: 'center', textTransform: 'capitalize' }}>
                  {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={handleNextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#555' }}>&gt;</button>
              </div>

              {/* Filtros de Pendência (Checkboxes Compactos) */}
              <div className="filter-area" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', fontWeight: 500, color: '#444' }}>
                  <input type="checkbox" checked={filterPendingFuture} onChange={e => setFilterPendingFuture(e.target.checked)} />
                  A Realizar
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', fontWeight: 500, color: '#444' }}>
                  <input type="checkbox" checked={filterPendingPast} onChange={e => setFilterPendingPast(e.target.checked)} />
                  Não Realizados
                </label>
              </div>

              {/* Filtros de Setores e Auditores (Compacto, alinhado à direita) */}
              <div className="filter-tabs-container" style={{ display: 'flex', gap: '15px', margin: 0, padding: 0, width: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <div className="unidade-tabs" style={{ gap: '5px' }}>
                  {availableSectors.map((s) => {
                    const isActive = activeSectors.includes(s);
                    return (
                      <button
                        key={s}
                        className={`auditor-checkbox-item ${isActive ? 'active' : ''}`}
                        style={{
                          backgroundColor: isActive ? '#007bff' : '#fff',
                          color: isActive ? '#fff' : '#666',
                          borderColor: isActive ? '#007bff' : '#dee2e6'
                        }}
                        onClick={() => toggleSetor(s)}
                      >
                        {isActive && <span className="auditor-color-dot" style={{ backgroundColor: 'white' }}></span>}
                        {!isActive && <span className="auditor-color-dot" style={{ backgroundColor: '#007bff' }}></span>}
                        {s}
                      </button>
                    );
                  })}
                </div>
                <div style={{ width: '1px', background: '#eee', height: '20px' }}></div>
                <div className="auditor-tabs" style={{ gap: '5px' }}>
                  {auditoresConfig.map((a) => {
                    const isActive = activeAuditores.includes(a.name);
                    return (
                      <button
                        key={a.id}
                        className={`auditor-checkbox-item ${isActive ? 'active' : ''}`}
                        style={{
                          backgroundColor: isActive ? a.color : '#fff',
                          color: isActive ? '#fff' : '#666',
                          borderColor: isActive ? a.color : '#dee2e6'
                        }}
                        onClick={() => toggleAuditor(a.name)}
                      >
                        {isActive && <span className="auditor-color-dot" style={{ backgroundColor: 'white' }}></span>}
                        {!isActive && <span className="auditor-color-dot" style={{ backgroundColor: a.color }}></span>}
                        {a.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="agenda-week-header">
              <div>Segunda</div><div>Terça</div><div>Quarta</div><div>Quinta</div><div>Sexta</div>
            </div>

            <div className="agenda-grid-monthly">
              {monthDays.map((day) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const dayString = day.toISOString().split('T')[0];
                const isPast = day < new Date().setHours(0, 0, 0, 0);

                // FILTRO DE ITENS - ESTRATEGIA DEBUG
                // Em vez de filtrar e remover, vamos classificar para o render
                const todosItensDoDia = auditorias.filter(a => a.data === dayString);

                const itemsProcessados = todosItensDoDia.map(a => {
                  let rejectionReason = null;

                  // 1. Filtro Setor
                  if (a.setor && !activeSectors.includes(a.setor)) {
                    rejectionReason = `Setor: ${a.setor}`;
                  }

                  // 2. Filtro Auditor
                  const auds = a.auditores || (a.auditor ? [a.auditor] : []);
                  if (!rejectionReason) {
                    const temAuditorAtivo = auds.some(audit => activeAuditores.includes(audit));
                    if (!temAuditorAtivo) {
                      rejectionReason = `Auditor: ${auds.join(', ')}`;
                    }
                  }

                  // 3. Filtros de Pendência (A Realizar / Não Realizados)
                  const isFilterActive = filterPendingFuture || filterPendingPast;

                  if (!rejectionReason && isFilterActive) {
                    const isPendingFuture = !isPast && a.status !== 'Feito' && a.status !== 'Reagendado';
                    const isPendingPast = isPast && (a.status === 'Não Realizado' || !a.status);

                    let matchesFuture = filterPendingFuture && isPendingFuture;
                    let matchesPast = filterPendingPast && isPendingPast;

                    if (!matchesFuture && !matchesPast) {
                      if (!filterPendingFuture && isPendingFuture) rejectionReason = 'Filtro: Futuro OFF';
                      else if (!filterPendingPast && isPendingPast) rejectionReason = 'Filtro: Passado OFF';
                      else rejectionReason = 'Filtro: Status/Data';
                    }
                  }

                  return { ...a, type: 'auditoria', rejectionReason };
                });

                // Filtra os que devem aparecer (sem reject ou debugMode on)
                const itemsVisible = itemsProcessados.filter(item => !item.rejectionReason || debugMode);

                const bloqueiosDoDia = bloqueios.filter((b) =>
                  b.data === dayString && activeAuditores.includes(b.auditor)
                ).map(b => ({ ...b, type: 'bloqueio' }));

                const itemsDoDia = [...itemsVisible, ...bloqueiosDoDia].sort((a, b) =>
                  (a.horario || '00:00').localeCompare(b.horario || '23:59')
                );

                return (
                  <div key={day.toISOString()} className={`day-cell ${isCurrentMonth ? '' : 'not-current-month'}`}>
                    <div className="day-cell-header">
                      {day.getDate()}
                      {debugMode && <small style={{ fontSize: '9px', color: 'red' }}> ({itemsVisible.length})</small>}
                    </div>
                    <div className="day-cell-body">
                      {itemsDoDia.map((item) => {
                        if (item.isManualPlaceholder) return null;

                        if (item.rejectionReason) {
                          return (
                            <div key={item.id} style={{
                              background: '#ffe6e6',
                              border: '1px solid red',
                              fontSize: '9px',
                              padding: '2px',
                              marginBottom: '2px',
                              color: 'red'
                            }}>
                              ❌ {item.rejectionReason} <br />
                              <strong>{item.auditor}</strong> - {item.setor} - {item.data}
                            </div>
                          );
                        }

                        if (item.type === 'bloqueio') {
                          return (
                            <div key={item.id} className="bloqueio-card" onClick={() => isCurrentMonth && handleDesbloquearDia(item.data, item.auditor)} title="Clique para desbloquear">
                              <strong>{item.auditor}</strong>: {item.motivo} 🗑️
                            </div>
                          );
                        }

                        // Renderizar Auditoria
                        const auds = item.auditores || (item.auditor ? [item.auditor] : []);
                        const firstAuditor = auds[0];
                        const corBorda = firstAuditor ? getCorAuditor(firstAuditor) : '#aaa';

                        return (
                          <div
                            key={item.id}
                            className={`auditoria-card ${getAuditoriaStatusClass(item)}`}
                            style={{ borderLeftColor: isCurrentMonth ? corBorda : '#aaa' }}
                            onClick={() => isCurrentMonth && handleOpenModalForEdit(item)}
                          >
                            <div className="auditoria-card-header">
                              <strong>{item.horario}</strong> - {item.descricao || item.auditoria}
                            </div>
                            <div className="auditoria-card-body">
                              <p><strong>Participante(s):</strong> {auds.join(', ')}</p>
                              {item.setor && <p><strong>Setor:</strong> {item.setor}</p>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
                              <button
                                onClick={(e) => handleDuplicateAuditoria(e, item)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                                title="Duplicar Reunião Externa"
                              >
                                📋
                              </button>
                            </div>
                            {item.reagendadoVezes > 0 && <div className="reagendado-aviso">Reagendado {item.reagendadoVezes}x</div>}
                            {item.justificativaNaoRealizado && (
                              <div className="justificativa-balao"><strong>Motivo:</strong> {item.justificativaNaoRealizado}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      <DeadlineReminder
        isTodayOpen={isTodayOpen}
        isWeekOpen={isWeekOpen}
        todayItems={todayItems}
        weekItems={weekItems}
        onCloseTodayPopup={closeTodayPopup}
        onCloseWeekPopup={closeWeekPopup}
        onDismissToday={dismissToday}
        type="event"
      />

      <NotificationsPopup
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        onReagendar={handleReagendar}
      />
    </div >
  );
}

export default AgendaHomecare;
